import type { QueryClient } from "@tanstack/react-query"
import { createFileRoute, stripSearchParams } from "@tanstack/react-router"
import type { FinancialsWindow } from "@/api/types"
import { CompanyDetailPage } from "@/components/pages/companies/detail"
import { companyVisitsOptions } from "@/hooks/queries/analytics"
import {
	companyContactsOptions,
	companyContractsOptions,
	companyEuetsOptions,
	companyFinanceSummaryOptions,
	companyKycOptions,
	companyMappingsOptions,
	companyOptions,
	companyRevenueOptions,
	getCompanyMapping,
	meetingsOptions,
} from "@/hooks/queries/companies"
import {
	registryAccountCountriesOptions,
	registryAccountStatsOptions,
} from "@/hooks/queries/registries"
import {
	pendingSummaryOptions,
	tradesTotalsOptions,
} from "@/hooks/queries/trading"
import { useTabNavigation } from "@/hooks/use-tab-navigation"
import {
	DEFAULT_FINANCIALS_WINDOW,
	DEFAULT_PAGE_SIZE,
	isFinancialsWindow,
} from "@/lib/constants"

export const COMPANY_DETAIL_TABS = [
	"finance",
	"relationship",
	"trades",
	"euets",
	"registry",
	"kyc",
	"fueleu",
	"fleet",
] as const
export type CompanyDetailTab = (typeof COMPANY_DETAIL_TABS)[number]
type Tab = CompanyDetailTab

function isTab(value: unknown): value is Tab {
	return (
		typeof value === "string" &&
		(COMPANY_DETAIL_TABS as readonly string[]).includes(value)
	)
}

type TabPrefetcher = (
	qc: QueryClient,
	id: number,
	revenueWindow: FinancialsWindow,
) => void | Promise<void>

const TAB_PREFETCHERS: Partial<Record<Tab, TabPrefetcher>> = {
	finance: async (qc, id, revenueWindow) => {
		qc.prefetchQuery(
			companyContractsOptions(id, { page: 1, pageSize: DEFAULT_PAGE_SIZE }),
		)
		qc.prefetchQuery(companyRevenueOptions(id, revenueWindow))
		if (!(await getCompanyMapping(qc, id, "business_central"))) return
		qc.prefetchQuery(companyFinanceSummaryOptions(id))
	},
	relationship: (qc, id) => {
		qc.prefetchQuery(meetingsOptions({ companyId: id, page: 1, pageSize: 3 }))
		qc.prefetchQuery(companyContactsOptions(id))
	},
	trades: async (qc, id) => {
		if (!(await getCompanyMapping(qc, id, "trading"))) return
		qc.prefetchQuery(tradesTotalsOptions("all", id))
		qc.prefetchQuery(pendingSummaryOptions(id))
	},
	euets: async (qc, id) => {
		qc.prefetchQuery(companyEuetsOptions(id))
		const externalId = (await getCompanyMapping(qc, id, "euets"))?.externalId
		if (!externalId) return
		qc.prefetchQuery(companyVisitsOptions("euets", externalId))
	},
	registry: (qc, id) => {
		qc.prefetchQuery(registryAccountStatsOptions(id))
		qc.prefetchQuery(registryAccountCountriesOptions(id))
	},
	kyc: (qc, id) => qc.prefetchQuery(companyKycOptions(id)),
}

export const Route = createFileRoute("/_authenticated/companies/$companyId")({
	validateSearch: (
		search: Record<string, unknown>,
	): {
		tab?: Tab
		window?: FinancialsWindow
		accountId?: number
		meetingId?: string
		contractId?: number
	} => ({
		tab: isTab(search.tab) ? search.tab : undefined,
		window: isFinancialsWindow(search.window) ? search.window : undefined,
		accountId:
			typeof search.accountId === "number" && Number.isFinite(search.accountId)
				? search.accountId
				: undefined,
		meetingId:
			typeof search.meetingId === "string" ? search.meetingId : undefined,
		contractId:
			typeof search.contractId === "number" &&
			Number.isFinite(search.contractId)
				? search.contractId
				: undefined,
	}),
	search: {
		middlewares: [stripSearchParams({ window: DEFAULT_FINANCIALS_WINDOW })],
	},
	loaderDeps: ({ search }) => ({ tab: search.tab, window: search.window }),
	loader: async ({ context, params, deps }) => {
		const id = Number(params.companyId)
		if (id <= 0) return
		const qc = context.queryClient
		qc.prefetchQuery(companyOptions(id))
		qc.prefetchQuery(companyMappingsOptions(id))
		await TAB_PREFETCHERS[deps.tab ?? "finance"]?.(
			qc,
			id,
			deps.window ?? DEFAULT_FINANCIALS_WINDOW,
		)
	},
	component: CompanyDetailRoute,
})

function CompanyDetailRoute() {
	const { companyId } = Route.useParams()
	const { tab: tabSearch } = Route.useSearch()
	const companyName = (window.history.state?.companyName as string) || undefined
	const navigate = Route.useNavigate()
	const { tab, onTabChange } = useTabNavigation(navigate, "finance", tabSearch)

	return (
		<CompanyDetailPage
			companyId={Number(companyId)}
			companyName={companyName}
			tab={tab}
			onTabChange={onTabChange}
		/>
	)
}
